import React, { useEffect, useState } from 'react';
import { Box, Typography, MuiLink } from 'components/mui';
import { Skeleton } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ROUTE_CUSTOMERS } from 'constants/routerPath';
import axiosInstance from 'api/axiosInstance';
import { Case } from 'containers/pages/cases/types';

interface CaseListRowDetailProps {
  caseItem: Case;
  formatDate: (dateStr: string) => string;
}

const labelSx = {
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  fontSize: '0.65rem',
} as const;

function CaseListRowDetail({ caseItem, formatDate }: CaseListRowDetailProps) {
  const navigate = useNavigate();
  const [fullCase, setFullCase] = useState<Case | null>(null);

  useEffect(() => {
    let cancelled = false;
    axiosInstance.get(`/api/cantrackv3/ReadCase?id=${caseItem.Id}`)
      .then((res) => {
        if (!cancelled && res.data) setFullCase(res.data);
      })
      .catch((err) => console.error('[CaseListRowDetail] Failed to fetch case:', err));
    return () => { cancelled = true; };
  }, [caseItem.Id]);

  const data = fullCase ?? caseItem;

  const groupPath = [data.GroupTitle, data.ProjectTitle, data.DeliverableTitle]
    .filter(Boolean)
    .join(' / ');

  return (
    <Box
      sx={{
        px: 3,
        py: 2,
        bgcolor: 'action.hover',
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
          gap: 1.5,
        }}
      >
        <Box>
          <Typography variant="caption" color="text.disabled" sx={labelSx}>Case ID</Typography>
          <Typography variant="body2" sx={{ mt: 0.25 }}>{data.Id}</Typography>
        </Box>

        <Box>
          <Typography variant="caption" color="text.disabled" sx={labelSx}>Customer</Typography>
          <Box sx={{ mt: 0.25 }}>
            {data.CustomerId ? (
              <MuiLink
                component="button"
                variant="body2"
                onClick={() => navigate(`${ROUTE_CUSTOMERS}/${data.CustomerId}`)}
                sx={{ cursor: 'pointer', textDecoration: 'none', color: 'primary.main', '&:hover': { textDecoration: 'underline' } }}
              >
                {data.CustomerName}
              </MuiLink>
            ) : (
              <Typography variant="body2">{data.CustomerName || 'N/A'}</Typography>
            )}
          </Box>
        </Box>

        <Box>
          <Typography variant="caption" color="text.disabled" sx={labelSx}>Consultant</Typography>
          <Typography variant="body2" sx={{ mt: 0.25 }}>{data.ConsultantName || 'N/A'}</Typography>
        </Box>

        <Box>
          <Typography variant="caption" color="text.disabled" sx={labelSx}>Primary Contact</Typography>
          <Typography variant="body2" sx={{ mt: 0.25 }}>{data.PrimaryContactName || 'N/A'}</Typography>
        </Box>

        <Box>
          <Typography variant="caption" color="text.disabled" sx={labelSx}>Status</Typography>
          <Typography variant="body2" sx={{ mt: 0.25 }}>
            {data.StatusTitle || 'N/A'}
            {data.StatusComment && (
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                ({data.StatusComment})
              </Typography>
            )}
          </Typography>
        </Box>

        <Box>
          <Typography variant="caption" color="text.disabled" sx={labelSx}>Group / Project / Deliverable</Typography>
          <Typography variant="body2" sx={{ mt: 0.25 }}>{groupPath || 'N/A'}</Typography>
        </Box>

        <Box>
          <Typography variant="caption" color="text.disabled" sx={labelSx}>Time Spent</Typography>
          {!fullCase ? (
            <Skeleton variant="text" width={40} sx={{ mt: 0.25 }} />
          ) : (
            <Typography variant="body2" sx={{ mt: 0.25 }}>{data.TimeSpent || '0'}</Typography>
          )}
        </Box>

        <Box>
          <Typography variant="caption" color="text.disabled" sx={labelSx}>Activities</Typography>
          {!fullCase ? (
            <Skeleton variant="text" width={30} sx={{ mt: 0.25 }} />
          ) : (
            <Typography variant="body2" sx={{ mt: 0.25 }}>{data.NumberOfActivities ?? 0}</Typography>
          )}
        </Box>

        <Box>
          <Typography variant="caption" color="text.disabled" sx={labelSx}>Created</Typography>
          <Typography variant="body2" sx={{ mt: 0.25 }}>{formatDate(data.CreatedOn) || 'N/A'}</Typography>
        </Box>

        <Box>
          <Typography variant="caption" color="text.disabled" sx={labelSx}>Updated</Typography>
          <Typography variant="body2" sx={{ mt: 0.25 }}>{formatDate(data.UpdatedOn) || 'N/A'}</Typography>
        </Box>
      </Box>

      {data.Description && (
        <Box sx={{ mt: 1.5 }}>
          <Typography variant="caption" color="text.disabled" sx={labelSx}>Description</Typography>
          <Box
            sx={{
              mt: 0.25,
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              color: 'text.secondary',
              typography: 'body2',
              '& p': { m: 0 },
              '& img': { maxWidth: '100%', height: 'auto' },
            }}
            dangerouslySetInnerHTML={{ __html: data.Description }}
          />
        </Box>
      )}
    </Box>
  );
}

export default CaseListRowDetail;
